import {
  EMPTY_VALUE,
  HCBB_DEFAULT_DISTANCE,
  HCBB_DISTANCE_OPTIONS,
} from "@/constants";
import ScreenRouteNames from "@/constants/screen-route-names";
import { getOS } from "@/lib/os-detector";
import type Distance from "@/model/distance";
import type {
  HcbbFacilityResult,
  HcbbPriceHighlight,
  HcbbProfessionalResult,
  HcbbRecentSearch,
} from "@/model/hcbb-cost-estimator";
import type Place from "@/model/place";
import type { PlaceMarker } from "@/model/place";
import type Procedure from "@/model/procedure";
import type { HcbbSuggestionKind } from "@/model/hcbb-cost-estimator";
import { INITIAL_HCBB_ADVANCED_FILTER_VALUES } from "@/store/hcbb-cost-estimator/hcbb-advanced-filters-slice";
import type { HcbbAdvancedFilters } from "@/store/hcbb-cost-estimator/hcbb-advanced-filters-slice";
import type { HcbbResultTab } from "@/store/hcbb-cost-estimator/hcbb-result-view-slice";
import type { OptionType } from "@/types";

/**
 * The search the enhanced cost estimator carries from the search page to the
 * results page. The URL is the transport (not router `state`) so that a
 * refresh, a back/forward step, or a shared link rebuilds the same search.
 */
export type HcbbSearch = {
  /**
   * The chosen suggestion, held in the shared `Procedure` shape (`code` = the
   * HCBB id, `description` = its label) whatever its kind, so the existing
   * slice and autocomplete keep working. `selectionKind` says what it really
   * is, which is what picks the results endpoint.
   */
  procedure: Procedure | null;
  selectionKind: HcbbSuggestionKind;
  targetLocation: Place | null;
  distance: Distance;
};

/**
 * The part of a search the cost-details endpoint is keyed by. All three matter:
 * the same procedure priced from a different location or radius is a different
 * result set, not a cached one.
 */
export type HcbbCostSearch = Pick<
  HcbbSearch,
  "procedure" | "targetLocation" | "distance"
>;

export const DEFAULT_SELECTION_KIND: HcbbSuggestionKind = "procedure";

const SELECTION_KINDS: HcbbSuggestionKind[] = [
  "procedure",
  "doctor",
  "facility",
  "specialty",
];

const HcbbSearchParam = {
  PROCEDURE_CODE: "procedure",
  PROCEDURE_DESCRIPTION: "procedureName",
  PLACE_ID: "placeId",
  PLACE_DESCRIPTION: "place",
  LAT: "lat",
  LNG: "lng",
  DISTANCE_ID: "distance",
  SELECTION_KIND: "kind",
  POSTAL_CODE: "postalCode",
} as const;

export const HCBB_COST_ESTIMATOR_PATH = `/${ScreenRouteNames.HCBB_COST_ESTIMATOR.path}`;

export const HCBB_COST_ESTIMATOR_RESULT_PATH = `${HCBB_COST_ESTIMATOR_PATH}/${ScreenRouteNames.HCBB_COST_ESTIMATOR_RESULT.path}`;

export const HCBB_FACILITY_DETAIL_PATH = `${HCBB_COST_ESTIMATOR_RESULT_PATH}/${ScreenRouteNames.HCBB_FACILITY_DETAIL.path}`;

export const HCBB_PROVIDER_DETAIL_PATH = `${HCBB_COST_ESTIMATOR_RESULT_PATH}/${ScreenRouteNames.HCBB_PROVIDER_DETAIL.path}`;

/**
 * Facility rows revealed per page.
 *
 * Facilities arrive priced and complete, so that list is grown in place:
 * every row is already in memory and View More only reveals more of it. The
 * providers tab cannot work that way — see `PROFESSIONAL_BATCH_SIZE`, which
 * is a limit of the details endpoint rather than a display choice.
 */
export const FACILITY_PAGE_SIZE = 10;

const SUGGESTION_ID_PREFIX = new RegExp(`^(${SELECTION_KINDS.join("|")})-`);

/**
 * The bare HCBB id behind a picked suggestion.
 *
 * `HcbbSuggestion.id` carries a kind prefix so ids stay unique across the four
 * dropdown groups, and that prefixed id is what round-trips through the URL as
 * `procedure.code`. The estimate endpoint wants the id on its own — the kind
 * travels separately as `selectionKind`.
 */
export const toHcbbProcedureId = (procedureCode: string): string =>
  procedureCode.replace(SUGGESTION_ID_PREFIX, "");

/** A search is only runnable once both a procedure and a location are picked. */
export const isHcbbSearchComplete = (search: Partial<HcbbSearch>): boolean =>
  Boolean(search.procedure && search.targetLocation && search.distance);

export const encodeHcbbSearch = ({
  procedure,
  selectionKind,
  targetLocation,
  distance,
}: HcbbSearch): URLSearchParams => {
  const params = new URLSearchParams();

  if (procedure) {
    params.set(HcbbSearchParam.PROCEDURE_CODE, procedure.code);
    params.set(HcbbSearchParam.PROCEDURE_DESCRIPTION, procedure.description);
    params.set(HcbbSearchParam.SELECTION_KIND, selectionKind);
  }

  if (targetLocation) {
    params.set(HcbbSearchParam.PLACE_ID, targetLocation.place_id);
    params.set(HcbbSearchParam.PLACE_DESCRIPTION, targetLocation.description);
    params.set(HcbbSearchParam.LAT, String(targetLocation.lat));
    params.set(HcbbSearchParam.LNG, String(targetLocation.lng));
    if (targetLocation.postal_code) {
      params.set(HcbbSearchParam.POSTAL_CODE, targetLocation.postal_code);
    }
  }

  if (distance) {
    params.set(HcbbSearchParam.DISTANCE_ID, String(distance.id));
  }

  return params;
};

export const buildHcbbResultUrl = (search: HcbbSearch): string =>
  `${HCBB_COST_ESTIMATOR_RESULT_PATH}?${encodeHcbbSearch(search).toString()}`;

const toCoordinate = (value: string | null): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const decodeHcbbSearch = (params: URLSearchParams): HcbbSearch => {
  const procedureCode = params.get(HcbbSearchParam.PROCEDURE_CODE);
  const placeId = params.get(HcbbSearchParam.PLACE_ID);
  const distanceId = Number(params.get(HcbbSearchParam.DISTANCE_ID));
  const kind = params.get(HcbbSearchParam.SELECTION_KIND);

  return {
    selectionKind:
      SELECTION_KINDS.find((option) => option === kind) ??
      DEFAULT_SELECTION_KIND,
    procedure: procedureCode
      ? {
          code: procedureCode,
          description:
            params.get(HcbbSearchParam.PROCEDURE_DESCRIPTION) ?? procedureCode,
        }
      : null,
    // `!== null` on purpose, not truthy: a recent search rebuilds its location
    // from a zip alone, so `place_id` round-trips as `""` — which `encodeHcbbSearch`
    // still writes to the URL. A truthy check read that empty-but-present param
    // as "no location was ever encoded" and dropped the whole location, which
    // then disabled the results page's `/estimate` query entirely.
    targetLocation:
      placeId !== null
        ? {
            place_id: placeId,
            description: params.get(HcbbSearchParam.PLACE_DESCRIPTION) ?? "",
            reference: "",
            lat: toCoordinate(params.get(HcbbSearchParam.LAT)),
            lng: toCoordinate(params.get(HcbbSearchParam.LNG)),
            postal_code: params.get(HcbbSearchParam.POSTAL_CODE) ?? "",
          }
        : null,
    distance:
      HCBB_DISTANCE_OPTIONS.find((option) => option.id === distanceId) ??
      HCBB_DEFAULT_DISTANCE,
  };
};

/**
 * A recent-search row, back in the shape the results page reads.
 *
 * The endpoint carries no place id or coordinates, so the location is
 * rebuilt from the zip alone — its `description` is the row's `address`
 * (the typeahead text the member actually saw, falling back to the zip in
 * `toRecentSearch` when the endpoint sends none). The procedure's
 * `description` is `displayName` the same way, and the radius is matched by
 * mileage rather than by id, the same way `decodeHcbbSearch` falls back to
 * the default when it cannot match.
 */
export const toHcbbSearchFromRecent = (
  recent: HcbbRecentSearch,
): HcbbSearch => ({
  selectionKind: DEFAULT_SELECTION_KIND,
  procedure: {
    code: recent.procedureId,
    description: recent.displayName,
  },
  targetLocation: {
    place_id: "",
    description: recent.address,
    reference: "",
    lat: 0,
    lng: 0,
    postal_code: recent.zipCode,
  },
  distance:
    HCBB_DISTANCE_OPTIONS.find((option) => option.value === recent.distance) ??
    HCBB_DEFAULT_DISTANCE,
});

/* ── result paging ───────────────────────────────────────────────────────── */

/**
 * Splits a list into fixed-size batches; the last one may be short.
 *
 * The professionals tab fetches its rows a batch at a time, so the batch
 * boundaries have to be a pure function of the reference list — the same list
 * must always split the same way, or a cached batch would answer for the wrong
 * ten references.
 */
export const toBatches = <T>(items: T[], size: number): T[][] =>
  size <= 0
    ? []
    : Array.from({ length: Math.ceil(items.length / size) }, (_, index) =>
        items.slice(index * size, index * size + size),
      );

/**
 * The rows with each id kept once, first occurrence winning.
 *
 * Batches are independent requests, so one can answer with a row another has
 * already returned. Two list items of the same key render the row twice and
 * warn, so the duplicate is dropped here rather than at the call site. `Map`
 * preserves insertion order, which is the order the batches arrived in.
 */
export const toUniqueById = <T extends { id: number }>(rows: T[]): T[] => {
  const byId = new Map<number, T>();

  rows.forEach((row) => {
    if (!byId.has(row.id)) byId.set(row.id, row);
  });

  return [...byId.values()];
};

/**
 * Identifies the result set a view belongs to.
 *
 * How much of each list the member has revealed is remembered against this
 * key, so a different search reads the defaults instead of inheriting the
 * previous search's depth — no reset step to forget anywhere.
 */
export const toHcbbResultKey = ({
  procedure,
  targetLocation,
  distance,
}: HcbbCostSearch): string =>
  `${procedure?.code}|${targetLocation?.place_id}|${distance?.id}`;

/* ── advanced search filters ─────────────────────────────────────────────── */

/**
 * Which tab the results are actually showing.
 *
 * Landing on an empty tab hides the results that did come back, so the data
 * picks the first one; the stored tab only speaks once the member has
 * actually chosen. Shared here because the filter panel needs the exact same
 * resolution as the tabs themselves to decide which fields to show.
 */
export const resolveHcbbActiveTab = (
  activeTab: HcbbResultTab | null,
  facilities: HcbbFacilityResult[],
): HcbbResultTab =>
  activeTab ?? (facilities.length > 0 ? "facilities" : "professionals");

const toFilterOption = (value: string): OptionType => ({
  displayName: value,
  value,
});

/** Distinct, non-blank values of one field, sorted for a stable dropdown order. */
const distinctFilterOptions = <T>(
  rows: T[],
  read: (row: T) => string | null | undefined,
): OptionType[] => {
  const values = new Set<string>();
  rows.forEach((row) => {
    const value = read(row);
    if (value) values.add(value);
  });
  return [...values].sort((a, b) => a.localeCompare(b)).map(toFilterOption);
};

export type HcbbFilterOptions = {
  facilityNetworkNames: OptionType[];
  facilityTypes: OptionType[];
  professionalNetworkNames: OptionType[];
  professionalFacilityTypes: OptionType[];
  professionalSpecialties: OptionType[];
};

/**
 * The advanced-search dropdown options, built from the full `/estimate`
 * response rather than a separate lookup endpoint.
 *
 * Both lists carry every candidate's `specialty`/`networkName`/`facilityType`
 * up front — unlike the professionals list before this change, these options
 * no longer depend on any batch having been fetched via View More.
 */
export const computeHcbbFilterOptions = (
  facilities: HcbbFacilityResult[],
  professionals: HcbbProfessionalResult[],
): HcbbFilterOptions => ({
  facilityNetworkNames: distinctFilterOptions(
    facilities,
    (row) => row.networkName,
  ),
  facilityTypes: distinctFilterOptions(facilities, (row) => row.facilityType),
  professionalNetworkNames: distinctFilterOptions(
    professionals,
    (row) => row.networkName,
  ),
  professionalFacilityTypes: distinctFilterOptions(
    professionals,
    (row) => row.facilityType,
  ),
  professionalSpecialties: distinctFilterOptions(
    professionals,
    (row) => row.specialty,
  ),
});

/**
 * Below this many trimmed letters, a "Facility/Physician Name or NPI" query
 * is still being typed rather than searched — the same minimum the offices
 * and doctors tables' own search boxes use (`MIN_SEARCH_LENGTH` in
 * `hcbb-provider-offices.tsx`/`hcbb-facility-providers.tsx`), so the three
 * free-text searches on this feature agree on when a search actually starts.
 */
const MIN_HCBB_NAME_QUERY_LENGTH = 3;

const matchesQuery = (
  value: string | null | undefined,
  query: string,
): boolean => {
  const trimmedQuery = query.trim().toLowerCase();
  // A term shorter than the minimum is still being typed, so it matches
  // everything rather than nothing — the list would otherwise empty out on
  // the first keystroke or two, the same reasoning the offices/doctors
  // tables' own client-side searches use.
  return (
    trimmedQuery.length < MIN_HCBB_NAME_QUERY_LENGTH ||
    Boolean(value?.toLowerCase().includes(trimmedQuery))
  );
};

/** `npis` arrives as either one id or several — flattened to text either way for a substring match. */
const npisToText = (npis: string | string[]): string =>
  Array.isArray(npis) ? npis.join(" ") : npis;

export const applyHcbbFacilityFilters = (
  facilities: HcbbFacilityResult[],
  filters: HcbbAdvancedFilters,
): HcbbFacilityResult[] =>
  facilities.filter((facility) => {
    if (filters.networkName && facility.networkName !== filters.networkName) {
      return false;
    }
    if (
      filters.facilityType &&
      facility.facilityType !== filters.facilityType
    ) {
      return false;
    }
    if (
      !matchesQuery(facility.displayName, filters.facilityQuery) &&
      !matchesQuery(npisToText(facility.npis), filters.facilityQuery)
    ) {
      return false;
    }
    return true;
  });

export const applyHcbbProfessionalFilters = (
  professionals: HcbbProfessionalResult[],
  filters: HcbbAdvancedFilters,
): HcbbProfessionalResult[] =>
  professionals.filter((professional) => {
    if (
      filters.networkName &&
      professional.networkName !== filters.networkName
    ) {
      return false;
    }
    if (
      filters.facilityType &&
      professional.facilityType !== filters.facilityType
    ) {
      return false;
    }
    if (filters.specialty && professional.specialty !== filters.specialty) {
      return false;
    }
    // "Physician Name or NPI": matched against whichever the member typed.
    // The npi/professionalId half only became possible once the estimate's
    // full rows were parsed — HcbbProfessionalResult used to carry neither.
    if (
      !matchesQuery(professional.displayName, filters.physicianQuery) &&
      !matchesQuery(String(professional.npi), filters.physicianQuery) &&
      !matchesQuery(professional.professionalId, filters.physicianQuery)
    ) {
      return false;
    }
    return true;
  });

/** Every field's empty value, for undoing one applied-filter chip without touching the rest. */
export const emptyHcbbFilterValueFor = (
  key: keyof HcbbAdvancedFilters,
): HcbbAdvancedFilters[typeof key] => INITIAL_HCBB_ADVANCED_FILTER_VALUES[key];

/**
 * Whether one committed filter field is actually in effect — shared by the
 * toggle button's badge count and the applied-filter chips, so neither one
 * ever claims a field is "applied" that `matchesQuery` itself would still
 * treat as match-everything: a facility/physician query below the three-
 * letter search minimum hasn't narrowed anything yet, whatever it's holding.
 */
export const isHcbbFilterFieldActive = (
  key: keyof HcbbAdvancedFilters,
  value: HcbbAdvancedFilters[keyof HcbbAdvancedFilters],
): boolean => {
  if (typeof value !== "string") return false;
  const trimmed = value.trim();
  return key === "facilityQuery" || key === "physicianQuery"
    ? trimmed.length >= MIN_HCBB_NAME_QUERY_LENGTH
    : trimmed.length > 0;
};

/**
 * How many advanced-search criteria are currently applied, across every
 * field regardless of tab — what the filter toggle button's count badge
 * shows.
 */
export const countAppliedHcbbFilters = (filters: HcbbAdvancedFilters): number =>
  (Object.keys(filters) as (keyof HcbbAdvancedFilters)[]).filter((key) =>
    isHcbbFilterFieldActive(key, filters[key]),
  ).length;

/** Whether any criterion this tab actually reads from is set — drives the empty-state copy. */
export const hasRelevantHcbbFilters = (
  filters: HcbbAdvancedFilters,
  tab: HcbbResultTab,
): boolean =>
  tab === "facilities"
    ? Boolean(filters.networkName || filters.facilityType) ||
      isHcbbFilterFieldActive("facilityQuery", filters.facilityQuery)
    : Boolean(
        filters.networkName || filters.facilityType || filters.specialty,
      ) || isHcbbFilterFieldActive("physicianQuery", filters.physicianQuery);

/** One row's type-ahead label for the "Facility/Physician Name or ID" fields. */
export type HcbbNameSuggestion = {
  id: number;
  name: string;
  /** `EMPTY_VALUE` when the row has none on file. */
  npi: string;
};

/**
 * The first NPI worth showing, always coerced to a string — `npis` can
 * arrive as one value, several, or absent, and (per `HcbbProfessionalRef.npi`)
 * a single one can be a raw number rather than a string, so every candidate
 * is stringified before it's compared or returned.
 */
const firstUsableNpi = (
  npis: string | number | (string | number)[] | undefined,
): string => {
  const list = npis === undefined ? [] : Array.isArray(npis) ? npis : [npis];
  const usable = list
    .map((npi) => (npi === undefined || npi === null ? "" : String(npi).trim()))
    .find((npi) => npi !== "" && npi !== "NA");
  return usable ?? EMPTY_VALUE;
};

/** Suggestion rows for "Facility Name or ID", built from the facilities already on screen. */
export const toHcbbFacilitySuggestions = (
  facilities: HcbbFacilityResult[],
): HcbbNameSuggestion[] =>
  facilities.map((facility) => ({
    id: facility.id,
    name: facility.displayName,
    npi: firstUsableNpi(facility.npis),
  }));

/**
 * Suggestion rows for "Physician Name or ID", built from the full `/estimate`
 * professional list — not just the rows already fetched via View More, since
 * the estimate carries every candidate's name and npi up front.
 */
export const toHcbbProfessionalSuggestions = (
  professionals: HcbbProfessionalResult[],
): HcbbNameSuggestion[] =>
  professionals.map((professional) => ({
    id: professional.id,
    name: professional.displayName,
    npi: firstUsableNpi(professional.npi),
  }));

/**
 * Suggestions whose name or NPI contains the query — the type-ahead list
 * under "Facility/Physician Name or NPI". Empty below the same three-letter
 * minimum `matchesQuery` itself gates on, so the suggestion list and the
 * filter it feeds always agree on when a search has actually started.
 */
export const filterHcbbNameSuggestions = (
  suggestions: HcbbNameSuggestion[],
  query: string,
): HcbbNameSuggestion[] => {
  const trimmed = query.trim().toLowerCase();
  if (trimmed.length < MIN_HCBB_NAME_QUERY_LENGTH) return [];
  return suggestions.filter(
    (suggestion) =>
      suggestion.name.toLowerCase().includes(trimmed) ||
      suggestion.npi.toLowerCase().includes(trimmed),
  );
};

/* ── detail screens ──────────────────────────────────────────────────────── */

/**
 * The row a detail screen renders travels in router `state`, not in the URL.
 *
 * The search is still carried by the URL — that rule is unchanged — but a
 * single row cannot be: it is far too large for a query string, and an id
 * could not be resolved after a refresh anyway, because the estimate and its
 * batches are cached in memory only. A detail URL opened without a row in
 * state therefore redirects back to the results it belongs to.
 *
 * What travels here is directory data — a name, an address, a phone number.
 * Nothing about the member belongs in `history.state`.
 */
const toDetailRow = (value: unknown): Record<string, unknown> | null => {
  if (typeof value !== "object" || value === null) return null;

  const row = value as Record<string, unknown>;
  return typeof row.id === "number" && typeof row.displayName === "string"
    ? row
    : null;
};

/** The facility a results row pushed, or null when there is nothing to show. */
export const readHcbbFacilityState = (
  state: unknown,
): HcbbFacilityResult | null => {
  if (typeof state !== "object" || state === null) return null;

  const row = toDetailRow((state as Record<string, unknown>).facility);
  return row as HcbbFacilityResult | null;
};

/**
 * The professional a results row pushed, or null.
 *
 * Read from its own key rather than a shared one, so the provider screen
 * cannot render a facility the other button pushed.
 */
export const readHcbbProfessionalState = (
  state: unknown,
): HcbbProfessionalResult | null => {
  if (typeof state !== "object" || state === null) return null;

  const row = toDetailRow((state as Record<string, unknown>).professional);
  return row as HcbbProfessionalResult | null;
};

/**
 * The estimate's own priced benchmark, pushed alongside a facility.
 *
 * Carried in state rather than re-fetched: the facility screen's doctor list
 * needs the estimate's `majorGroup` in its request, and asking the results
 * page for the value it already has avoids a second `/estimate` call (and the
 * wait for it) purely to recover one field. Not a detail row, so it skips
 * `toDetailRow`'s id/displayName shape check; a missing or malformed value
 * reads as null, same as a procedure HCBB never priced.
 */
export const readHcbbHighlightState = (
  state: unknown,
): HcbbPriceHighlight | null => {
  if (typeof state !== "object" || state === null) return null;

  const highlight = (state as Record<string, unknown>).highlight;
  return typeof highlight === "object" && highlight !== null
    ? (highlight as HcbbPriceHighlight)
    : null;
};

/* ── result formatting ───────────────────────────────────────────────────── */

const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

/** Row-level costs arrive as numbers, unlike the pre-formatted highlight. */
export const formatCurrency = (value: number): string =>
  currencyFormatter.format(value);

/** The payload uses "NA" as its empty sentinel rather than omitting the field. */
export const orEmptyValue = (value?: string | null): string =>
  !value || value === "NA" ? EMPTY_VALUE : value;

/**
 * Prices arrive pre-formatted ("$12,315"), and 0 is a legitimate amount — so
 * every check here tests for null rather than falsiness.
 */
export const parseMoney = (
  value: string | number | null | undefined,
): number | null => {
  if (value === null || value === undefined) return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;

  const numeric = value.replace(/[^0-9.-]/g, "");
  if (!/\d/.test(numeric)) return null;

  const parsed = Number(numeric);
  return Number.isFinite(parsed) ? parsed : null;
};

/* ── row actions ─────────────────────────────────────────────────────────── */

/**
 * A dialable form of the payload's phone number, or null when there is nothing
 * to dial. Punctuation is stripped because `tel:` handlers vary in what they
 * tolerate; a leading + is kept for international numbers.
 */
export const toTelHref = (phone?: string | null): string | null => {
  if (!phone || phone === "NA") return null;

  const dialable = phone.replace(/[^\d+]/g, "").replace(/(?!^)\+/g, "");
  return dialable.replace(/\D/g, "").length > 0 ? `tel:${dialable}` : null;
};

/**
 * Whether this device can actually place a call from a `tel:` link.
 *
 * Desktop OSes hand a `tel:` link to nothing a member can see happen, so the
 * result cards fall back to copying the number there instead of dialing.
 * Mirrors the mobile check in `lib/pdf-viewer-support.ts`.
 */
export const canPlaceCall = (): boolean => {
  const { isAndroid, isIOS } = getOS();
  return isAndroid || isIOS;
};

/** "~ 72 miles" → 72. The map card prints this under the marker. */
const parseMiles = (displayRadius?: string | null): number => {
  const miles = parseMoney(displayRadius);
  return miles === null ? 0 : Math.abs(miles);
};

/**
 * A marker for the shared `MapView`, or null when the row cannot be placed.
 *
 * Only facilities carry real coordinates today — every professional in the
 * payload comes back with "NA" for both — so this returns null far more often
 * than not, and callers must treat an unmappable row as a first-class case.
 */
export const toPlaceMarker = (row: {
  name: string;
  address: string;
  displayRadius: string;
  latitude: number | string;
  longitude: number | string;
}): PlaceMarker | null => {
  const lat = parseMoney(row.latitude);
  const lng = parseMoney(row.longitude);

  // 0,0 is the payload's other way of saying "unknown" — see `toCoordinate`,
  // which floors unparseable coordinates to 0 on the way out of the URL.
  if (lat === null || lng === null) return null;
  if (lat === 0 && lng === 0) return null;

  return {
    name: row.name,
    address: orEmptyValue(row.address),
    distance: parseMiles(row.displayRadius),
    pinSymbol: "Pin",
    color: "green",
    lat,
    lng,
  };
};
